import React from 'react'
import {
  Pagination as ShadcnPagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from './ui/pagination'
import { useLocation, useNavigate } from 'react-router-dom'

function paginate(lastPage: number, currentPage: number, delta = 2): number[] {
  const range = []
  const result: number[] = []

  for (let i = 1; i <= lastPage; i++) {
    range.push(i)
  }

  // Filter out pages that are not adjacent to the current page
  range.forEach((page) => {
    if (
      page === 1 || // Always include the first page
      page === lastPage || // Always include the last page
      (page >= currentPage - delta && page <= currentPage + delta) // Pages near the current page
    ) {
      result.push(page)
    } else if (result[result.length - 1] !== -1) {
      // Add ellipses for skipped ranges
      result.push(-1)
    }
  })

  return result
}

const Pagination = ({ lastPage }: { lastPage: number }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const currentPage = Number(searchParams.get('page')) || 1

  const createPageURL = (pageNumber: number) => {
    const params = new URLSearchParams(location.search)
    params.set('page', Math.max(Math.min(pageNumber, lastPage), 1).toString())
    return `${location.pathname}?${params.toString()}`
  }

  const handlePageChange = (pageNumber: number) => {
    navigate(createPageURL(pageNumber))
  }

  if (lastPage <= 1) {
    return null
  }

  return (
    <ShadcnPagination className="mt-4">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => handlePageChange(currentPage - 1)}
            aria-disabled={currentPage === 1}
          />
        </PaginationItem>

        {paginate(lastPage, currentPage).map((page) => (
          <PaginationItem key={page}>
            {page === -1 ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                isActive={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            onClick={() => handlePageChange(currentPage + 1)}
            aria-disabled={currentPage === lastPage}
          />
        </PaginationItem>
      </PaginationContent>
    </ShadcnPagination>
  )
}

export default Pagination
